<script setup lang="ts">
import {computed, onMounted, reactive, ref, watch} from 'vue'
import SplitButton from 'primevue/splitbutton'
import Button from 'primevue/button'
import Divider from 'primevue/divider'
import type {Quality} from '@/types/supplier-types'
import {Icon} from '@iconify/vue'
import type {BatchRoll} from '@/types/batch-types'
import ReturnFromSupplierEditRollListItem from '@/components/features/batches/form-elements/ReturnFromSupplierEditRollListItem.vue'
import EditRollsList from '@/components/features/batches/form-elements/EditRollsList.vue'
import {RollState} from "@/types/roll-types";

const props = withDefaults(
  defineProps<{
    quality?: Quality
    includeLength?: boolean
    includeMergeNo?: boolean
    includeSupplierNo?: boolean
    allowAddRolls?: boolean
    lengthLabel: string
    weightLabel: string
    disabled?: boolean
    includeInactiveRolls?: boolean
    showActions?: boolean
    modelValue: BatchRoll[]
    fabricYieldValue?: number
    fabricYieldUnit?: number
    availableRolls?: BatchRoll[]
  }>(),
  {
    includeLength: true,
    includeMergeNo: true,
    includeSupplierNo: false,
    allowAddRolls: false,
    lengthLabel: 'Length',
    weightLabel: 'Weight',
    disabled: true,
    includeInactiveRolls: true,
    showActions: true,
    fabricYieldValue: 0,
    fabricYieldUnit: 0,
    availableRolls: () => []
  }
)
const emit = defineEmits<{
  (event: 'update:modelValue', value: BatchRoll[]): void
  (event: 'totalsCalculated', value: { weight: number; lengthValue: number; totalPercentageWeightLostOrGained: number}): void
}>()

const filteredModelValue = computed(() => {
  if (!props.modelValue) return []

  return props.modelValue.filter(roll => {
    if (!roll.id || roll.rollState === undefined) return true

    const rollsSentToSupplier = props.availableRolls?.filter(availableRoll =>
        availableRoll.isActive && availableRoll.rollState === 1
    ) || []

    return rollsSentToSupplier.some(sentRoll => sentRoll.id === roll.id)
  })
})

const model = ref(filteredModelValue.value.map((roll) => reactive(roll)))
const showInactiveRolls = ref(false)
const showRemainingRolls = ref(false)
const initialWeight = ref(0);

const rollsSentToSupplier = computed(() => {
  return props.availableRolls?.filter(roll =>
      roll.isActive && roll.rollState === 1
  ) || []
})

const remainingRolls = computed(() => {
  return props.availableRolls?.filter(roll =>
      roll.isActive && roll.rollState !== 1
  ) || []
})

const remainingRollCount = computed(() => remainingRolls.value.length)

const totalWeight = computed(() => {
  return model.value.reduce((total, roll) => total + (roll.weight || 0), 0)
})

const totalLength = computed(() => {
  return model.value.reduce((total, roll) => total + (roll.lengthValue || 0), 0)
})

const weightPercentageChange = computed(() => {
  if (initialWeight.value === 0) return 0;
  return ((totalWeight.value - initialWeight.value) / initialWeight.value) * 100;
});

const removeRoll = (rollIndex: number) => {
  if (!model.value) return

  const roll = model.value[rollIndex]

  const rollToRemoveIndex = model.value.findIndex((r) => r === roll)

  model.value?.splice(rollToRemoveIndex, 1)
}

const addRolls = (numRolls: number) => {
  model.value = model.value || []

  const newRolls = Array.from({ length: numRolls }, () => ({
    rollNumber: '',
    weight: undefined,
    lengthValue: undefined,
    isActive: true,
    rollState: 1
  }))

  model.value.push(...newRolls)
}

const addRoll = () => addRolls(1)

const addRollOptions = [
  {
    label: 'Add 2 more Rolls',
    command: () => addRolls(2)
  },
  {
    label: 'Add 3 more Rolls',
    command: () => addRolls(3)
  },
  {
    label: 'Add 4 more Rolls',
    command: () => addRolls(4)
  }
]

//TODO: Find out why these are being commented out
// const activeRolls = computed(() => model.value ? model.value.filter(r => r.active) : [])
// const inactiveRolls = computed(() => model.value ? model.value.filter(r => !r.active) : [])
// const toggleShowInactive = () => (showInactiveRolls.value = !showInactiveRolls.value)

const calculateLength = (weight: number) => {
  if (props.fabricYieldValue && weight) {
    return weight * props.fabricYieldValue
  }
  return 0
}

const updateModelValue = (index: number, updatedRoll: BatchRoll) => {
  if (!model.value) return

  const newRolls = [...model.value]
  newRolls[index] = updatedRoll
  model.value = newRolls
}

const emitTotals = () => {
  emit('totalsCalculated', {
    weight: totalWeight.value,
    lengthValue: totalLength.value,
    totalPercentageWeightLostOrGained: weightPercentageChange.value
  })
}

watch(filteredModelValue, (newValue) => {
  model.value = newValue.map((roll) => reactive(roll))
}, { deep: true, immediate: true })

watch(model, (newValue) => {
  emit('update:modelValue', newValue)
  emitTotals()
}, { deep: true })

watch(() => props.fabricYieldValue, (newYield) => {
  if (newYield && model.value && model.value.length > 0) {
    model.value = model.value.map(roll => {
      if (roll.weight) {
        return {
          ...roll,
          lengthValue: roll.weight * newYield,
          lengthUnit: props.fabricYieldUnit
        }
      }
      return roll
    })
  }
}, { immediate: true })

onMounted(() => {
  const sentRolls = props.availableRolls?.filter(roll =>
      roll.rollState === RollState.Offsite
  ) || []

  initialWeight.value = sentRolls.reduce((total, roll) => total + (roll.weight || 0), 0)
  emitTotals()
})

</script>

<template>
  <div class="grid grid-cols-12 gap-x-20 gap-y-3">
    <div v-for="(roll, index) in model" :key="index" class="col-span-12">
      <h3 class="font-medium">Roll {{ index + 1 }}:</h3>
      <ReturnFromSupplierEditRollListItem
        v-model="model[index]"
        @update:modelValue="(updatedRoll) => updateModelValue(index, updatedRoll)"
        @remove="removeRoll(index)"
        :editable="!disabled"
        :show-actions="showActions"
        :fabricYieldValue="fabricYieldValue"
        :fabricYieldUnit="fabricYieldUnit"
        :availableRolls="rollsSentToSupplier"
      />
    </div>

    <div class="col-span-12" v-if="allowAddRolls">
      <SplitButton
        size="small"
        outlined
        label="Add Roll"
        @click="addRoll"
        :model="addRollOptions"
      />
    </div>

    <div class="col-span-12" v-if="remainingRollCount > 0">
      <div class="flex items-center gap-4 mb-4">
        <Button
            @click="showRemainingRolls = !showRemainingRolls"
            severity="secondary"
            outlined
            size="small"
            class="min-w-44 text-xs gap-2"
        >
          <template v-if="!showRemainingRolls">
            <span>Show Rolls from Previous Batch</span>
            <Icon icon="mdi:eye" class="text-xl rounded" />
          </template>
          <template v-else>
            <Icon icon="mdi:eye-off" class="text-xl rounded" />
            <span>Hide Rolls from Previous Batch</span>
          </template>
        </Button>

        <span class="text-sm text-gray-600">({{ remainingRollCount }} rolls not sent to supplier)</span>

        <Divider class="!my-3 flex-1" />

        <Button
            @click="showRemainingRolls = !showRemainingRolls"
            rounded
            size="small"
            class="!bg-primary-50 !border-primary-50 !text-primary-500 !w-8 !h-8 !p-0"
        >
          <Icon v-if="!showRemainingRolls" icon="mdi:chevron-down" class="text-4xl" />
          <Icon v-else icon="mdi:chevron-up" class="text-4xl" />
        </Button>
      </div>

      <div v-if="showRemainingRolls" class="bg-gray-50 p-4 rounded-lg">
        <h3 class="text-md font-semibold mb-3 text-gray-700">Rolls from Previous Batch (Not Sent to Supplier)</h3>
        <EditRollsList
            v-model="remainingRolls"
            :disabled="true"
            :includeInactiveRolls="false"
            :showActions="false"
        />
      </div>
    </div>

    <div class="col-span-12">
      <span>Weight Change:<span
          :class="weightPercentageChange > 0 ?
            'text-green-600' : 'text-red-600'"
          class="inline-flex items-center">{{ weightPercentageChange.toFixed(2) }}%
            <Icon :icon="weightPercentageChange > 0 ?
            'mdi:arrow-up' : 'mdi:arrow-down'" class="ml-1" />
        </span>
      </span>
    </div>
  </div>
</template>

<style scoped></style>